import { History, Save, Clock, Shield, Trash2, RotateCcw } from "lucide-react";
import { useMetaTags } from "../components/MetaTags";

function Section({ icon: Icon, title, children }: { icon: typeof History; title: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <div className="flex items-center gap-2.5 mb-3">
        <Icon size={20} className="text-indigo-600" />
        <h2 className="text-xl font-bold text-slate-900">{title}</h2>
      </div>
      {children}
    </section>
  );
}

export function DocsBackups() {
  useMetaTags({
    title: "OmniCanvas Backups & Version History",
    description:
      "How OmniCanvas protects your notes: automatic version-history snapshots, restore points, local-first storage, and encrypted cloud sync.",
    url: "https://omnicanvasnotes.com/docs/backups",
  });
  return (
    <div className="pt-28 pb-20">
      <div className="max-w-2xl mx-auto px-6">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 mb-4">
            Backups & Version History
          </h1>
          <p className="text-lg text-slate-600">
            OmniCanvas automatically backs up your notes so you can always recover your work.
          </p>
        </div>

        <Section icon={Clock} title="Continuous Snapshots">
          <p className="text-sm text-slate-600 leading-relaxed mb-3">
            Every time your notes sync to the cloud, OmniCanvas saves a snapshot of the previous
            state. During active editing, syncs happen roughly every 30 seconds, giving you granular
            undo history.
          </p>
          <p className="text-sm text-slate-600 leading-relaxed">
            <strong>Continuous snapshots are kept for 72 hours</strong> on all plans, including Free.
            This means you can recover from any mistake made in the last 3 days, down to the exact
            sync point.
          </p>
        </Section>

        <Section icon={History} title="Daily Snapshots">
          <p className="text-sm text-slate-600 leading-relaxed mb-3">
            After 72 hours, continuous snapshots are automatically consolidated into{" "}
            <strong>one snapshot per day</strong>. This keeps your history manageable while preserving
            meaningful recovery points.
          </p>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-3">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500">
                  <th className="pb-2 font-medium">Plan</th>
                  <th className="pb-2 font-medium">Daily Snapshots Kept</th>
                </tr>
              </thead>
              <tbody className="text-slate-700">
                <tr className="border-t border-slate-100">
                  <td className="py-2">Free</td>
                  <td className="py-2">3 per note</td>
                </tr>
                <tr className="border-t border-slate-100">
                  <td className="py-2">Pro and Team</td>
                  <td className="py-2">365 per note</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed">
            <strong>Every note always keeps at least 3 daily snapshots</strong>, regardless of plan
            or how old they are. If you haven't touched a note in a year, you'll still have 3
            recovery points. They're only replaced once newer daily snapshots are available.
          </p>
        </Section>

        <Section icon={Save} title="Manual Checkpoints">
          <p className="text-sm text-slate-600 leading-relaxed mb-3">
            Paid plans can save up to <strong>10 manual checkpoints per note</strong>. These are
            perfect for saving a known-good state before making big changes. Manual checkpoints are
            never automatically deleted.
          </p>
          <ul className="text-sm text-slate-600 space-y-2">
            <li className="flex items-start gap-2">
              <Save size={14} className="text-indigo-500 mt-0.5 shrink-0" />
              Open the Version History panel and click <strong>Save Checkpoint</strong>
            </li>
            <li className="flex items-start gap-2">
              <Trash2 size={14} className="text-indigo-500 mt-0.5 shrink-0" />
              Delete old checkpoints with the trash icon to free up slots
            </li>
          </ul>
        </Section>

        <Section icon={RotateCcw} title="Restoring a Version">
          <p className="text-sm text-slate-600 leading-relaxed mb-3">
            Restoring is always safe. Before applying the old version, OmniCanvas automatically
            snapshots the current state, so you can always undo a restore.
          </p>
          <ol className="text-sm text-slate-600 space-y-2 list-decimal list-inside">
            <li>Open the Version History panel (clock icon)</li>
            <li>Browse versions with thumbnail previews</li>
            <li>Click a version to select it</li>
            <li>Click <strong>Restore This Version</strong></li>
          </ol>
        </Section>

        <Section icon={Shield} title="How It Works Under the Hood">
          <div className="text-sm text-slate-600 leading-relaxed space-y-3">
            <p>
              Snapshots capture your note's title, full canvas state (drawings, shapes, text, images),
              and sticky notes. Other metadata like tags and folders are not versioned.
            </p>
            <p>
              A background job runs every 15 minutes to consolidate snapshots. It collapses continuous
              snapshots older than 72 hours into daily snapshots, then trims excess dailies based on
              your plan. Old snapshots are only removed once enough newer ones exist to replace them,
              so resuming edits on a long-dormant note won't lose your safety net.
            </p>
            <p>
              Sync conflict versions (when edits happen on two devices simultaneously) are preserved
              separately and never automatically deleted.
            </p>
          </div>
        </Section>
      </div>
    </div>
  );
}
